/**
 * Words that belong to no single screen.
 *
 * Network errors raised by the axios interceptor, and the handful of verbs that
 * appear on every form in the application. Anything used by exactly one screen
 * belongs in that screen's file instead — this one earns its place by being
 * genuinely shared, and it stops being useful the moment it becomes the place
 * strings go when nobody wants to decide.
 */

export const common = {
  // ---- Network -----------------------------------------------------
  // Shown by the axios interceptor rather than by a screen, which is why they
  // are phrased without reference to what the user was doing.
  'error.offline': 'Nema veze sa serverom. Provjerite internet konekciju.',
  'error.forbidden': 'Nemate pravo za ovu radnju.',
  'error.notFound': 'Traženi podatak nije pronađen.',
  'error.sessionExpired': 'Sesija je istekla. Prijavite se ponovo.',
  'error.tooManyRequests': 'Previše zahtjeva. Sačekajte trenutak.',
  'error.server': 'Greška na serveru. Pokušajte ponovo.',
  'error.generic': 'Došlo je do greške.',
  'error.boundary.title': 'Ova stranica se nije mogla prikazati',
  'error.boundary.body': 'Greška je zabilježena. Ostatak aplikacije i dalje radi — možete se vratiti i nastaviti.',
  'error.staleBuild.title': 'Objavljena je nova verzija',
  'error.staleBuild.body': 'Ova kartica je otvorena od ranije, pa dio aplikacije više nije dostupan. Osvježite stranicu — ništa nije izgubljeno.',
  'error.staleBuild.reload': 'Osvježi stranicu',
  'error.supportSessionExpired': 'Sesija podrške je istekla.',

  // ---- Verbs -------------------------------------------------------
  'action.save': 'Sačuvaj',
  'action.cancel': 'Otkaži',
  'action.close': 'Zatvori',
  'action.back': 'Nazad',
  'action.delete': 'Obriši',
  'action.edit': 'Izmijeni',
  'action.confirm': 'Potvrdi',
  'action.retry': 'Pokušaj ponovo',
  'action.search': 'Pretraga',
  'action.filter': 'Filter',
  'action.clear': 'Očisti',
  'action.more': 'Još',
  // Nosi ga strelica pored polja koje nudi prijedloge, a pojavljuje se svuda
  // gdje takvo polje stoji — otuda ovdje, uz ostale glagole.
  'action.showOptions': 'Prikaži ponuđeno',

  // ---- States ------------------------------------------------------
  'state.loading': 'Učitavanje…',
  'state.saving': 'Čuvanje…',
  'state.empty': 'Nema podataka.',
  'state.saved': 'Sačuvano.',
  // Polje koje nudi prijedloge ne zabranjuje ostalo, pa prazan spisak nije
  // greška. Druga rečenica to kaže, jer prazan okvir sam po sebi izgleda kao
  // kvar — tako je i prijavljen kad je ovdje stajao `<datalist>`.
  'state.noMatches': 'Nema rezultata. Ostaje ono što ste upisali.',

  // ---- Units -------------------------------------------------------
  // Interpolated, never concatenated: these three languages inflect the noun
  // after a number differently from English, and a glued "{n} + ' dana'" is a
  // sentence a translator cannot repair.
  'unit.days': '{count} dana',

  // ---- Relative time and units --------------------------------------
  /*
   * Rendered by `lib/utils.ts`, which is not a component — it translates
   * through the module-level `getActiveLocale()` the axios interceptor already
   * reads, exactly as `lib/api.ts` does.
   *
   * "prije 3 dana" reads faster to a supervisor scanning for what has been
   * sitting too long than a timestamp they have to subtract from today, which
   * is why these exist at all rather than a formatted date everywhere.
   */
  'time.justNow': 'upravo sada',
  'time.minutesAgo': 'prije {count} min',
  'time.hoursAgo': 'prije {count} h',
  'time.yesterday': 'juče',
  'time.daysAgo.one': 'prije {count} dan',
  'time.daysAgo.few': 'prije {count} dana',
  'time.daysAgo.many': 'prije {count} dana',
  'unit.minutesShort': '{count} min',
  'unit.hoursShort': '{count} h',
  'unit.daysLong.one': '{count} dan',
  'unit.daysLong.few': '{count} dana',
  'unit.daysLong.many': '{count} dana',
  // The convertible mark. Not translated — it is the currency's name, the same
  // in every language on this screen — but kept here so the position of the
  // symbol relative to the number can move if a locale ever needs it to.
  // The unit is a parameter, not part of the sentence: it belongs to the
  // company rather than to the language. See lib/currency.ts.
  'unit.money': '{amount} {currency}',
  'unit.hours': '{count} sati',

  // ---- Verzija -----------------------------------------------------
  // Oznaka verzije stoji u podnožju i na ekranu naloga zato da bi podrška
  // mogla pitati koju verziju osoba vidi umjesto da nagađa.
  //
  // `update.consequence` ide uz `error.staleBuild.*` iznad, ali nije isto:
  // one rečenice govore o kartici koja je već stala, pa smiju obećati da ništa
  // nije izgubljeno. Ova se javlja *prije* toga, dok neko možda popunjava
  // prijavu, pa mora reći šta osvježavanje košta.
  'version.label': 'Verzija',
  'update.consequence':
    'Osvježite kada završite ono što radite — nesačuvani unosi na ovom ekranu se gube, a prijave u redu čekanja ostaju.',

  // ---- Objašnjenja -------------------------------------------------
  // Dvije riječi koje nosi svaki `Hint` i svaki `HelpNote` u aplikaciji —
  // otuda ovdje, a ne u fajlu jednog ekrana. Sadržaj objašnjenja pripada
  // ekranu na kojem stoji; ovdje je samo okvir oko njega.
  'help.explain': 'Objašnjenje',
  'help.readMore': 'Detaljnije u uputstvu →',

  // ---- Izvoz i red čekanja ------------------------------------------
  // Ove poruke su ranije bile ukucane direktno u ExportButton i
  // useOfflineQueue, pa je čitalac na engleskom dobijao potvrdu na
  // bosanskom. Ovdje su jer ih pokreće dugme koje stoji na više ekrana.
  'export.started': 'Preuzimanje je pokrenuto.',
  'export.failed': 'Izvoz nije uspio.',

  'queue.sent.one': 'Sačuvana prijava je poslata.',
  'queue.sent.few': 'Poslate su {count} sačuvane prijave.',
  'queue.sent.many': 'Poslato je {count} sačuvanih prijava.',
  'queue.failed.one': 'Jedna prijava nije prihvaćena. Otvorite red čekanja.',
  'queue.failed.few': '{count} prijave nisu prihvaćene. Otvorite red čekanja.',
  'queue.failed.many': '{count} prijava nije prihvaćeno. Otvorite red čekanja.',
  'queue.nothing': 'Nema ničega za slanje.',
  'queue.discarded': 'Prijava je uklonjena iz reda čekanja.',

} as const

export type CommonSlice = Record<keyof typeof common, string>
